import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area,
  LineChart, Line,
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { MOIS_COURT, moisActifs } from '../utils/helpers';

/* ── Constantes ── */
const FO_RED    = '#CC0000';
const FO_YELLOW = '#FFD700';
const COLORS    = [FO_RED, '#1565c0', '#2e7d32', FO_YELLOW, '#6a1b9a', '#e65100', '#00838f', '#37474f', '#c62828', '#f9a825'];
const GENRE_COLORS = ['#e91e63', '#1565c0', '#9e9e9e'];
const MULT = { Mensuelle: 12, Trimestrielle: 4, Semestrielle: 2, Annuelle: 1 };

async function exportPNG(ref, filename) {
  if (!ref?.current) return;
  try {
    const c = await html2canvas(ref.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png'); a.download = filename; a.click();
  } catch (e) { console.error('PNG export:', e); }
}

function exportCSV(rows, filename) {
  if (!rows?.length) return;
  const hdrs = Object.keys(rows[0]);
  const csv  = [hdrs.join(','), ...rows.map(r => hdrs.map(h => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
  const a    = document.createElement('a');
  a.href     = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
  a.download = filename;
  a.click();
}

const Tip = ({ active, payload, label, money }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 7, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
      {label && <div style={{ fontWeight: 700, marginBottom: 4, color: '#333' }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#333' }}>{p.name} : <strong>{money ? `${p.value} €` : p.value}</strong></div>
      ))}
    </div>
  );
};

const KpiCard = ({ label, value, sub, color = FO_RED }) => (
  <div style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', border: '1px solid #e8e8e8', borderLeft: `4px solid ${color}` }}>
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .6, color: '#aaa', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: '#111', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#bbb', marginTop: 5 }}>{sub}</div>}
  </div>
);

const Block = ({ title, onCSV, onPNG, blockRef, children }) => (
  <div className="card mb-4" style={{ pageBreakInside: 'avoid' }} ref={blockRef}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <span style={{ fontWeight: 700, fontSize: 15 }}>{title}</span>
      <div style={{ display: 'flex', gap: 5 }} className="no-print">
        {onPNG && <button className="btn btn-outline btn-xs" onClick={onPNG} title="Exporter image PNG">🖼️ PNG</button>}
        {onCSV && <button className="btn btn-outline btn-xs" onClick={onCSV} title="Exporter CSV">⬇️ CSV</button>}
      </div>
    </div>
    {children}
  </div>
);

export default function Statistiques({ adherents: _adherents, etablissements: _etablissements, cotisations, comptabilite = {}, selectedYear, setYear, solde, setSolde, currentRole, currentUserEtabsIds }) {
  const etabSet = (currentRole === 'delegue' && currentUserEtabsIds?.length > 0) ? new Set(currentUserEtabsIds) : null;
  const adherents = etabSet ? _adherents.filter(a => etabSet.has(a.etablissementId)) : _adherents;
  const etablissements = etabSet ? _etablissements.filter(e => etabSet.has(e.id)) : _etablissements;
  const [mainTab,  setMainTab]  = useState('adherents');
  const [fConv,    setFConv]    = useState('all');
  const [fEtab,    setFEtab]    = useState('all');
  const [fStatut,  setFStatut]  = useState('Actif');
  const [soldeEdit,    setSoldeEdit]    = useState(String(solde || ''));
  const [exportingPDF, setExportingPDF] = useState(false);
  const dashRef  = useRef();
  const refConv  = useRef(); const refGenre  = useRef();
  const refEtab  = useRef(); const refMetier = useRef();
  const refAnc   = useRef(); const refMode   = useRef();
  const refEvol  = useRef(); const refMulti  = useRef();
  const refFin   = useRef(); const refPay    = useRef();
  const refCotCcn= useRef(); const refTaux   = useRef();

  const yearCots     = cotisations[selectedYear]     || {};
  const prevYearCots = cotisations[selectedYear - 1] || {};

  const conventions = useMemo(() => [...new Set(adherents.map(a => a.convention).filter(Boolean))].sort(), [adherents]);

  const filtered = useMemo(() => adherents.filter(a => {
    if (fStatut !== 'all' && a.statut !== fStatut) return false;
    if (fConv   !== 'all' && a.convention !== fConv) return false;
    if (fEtab   !== 'all' && a.etablissementId !== fEtab) return false;
    return true;
  }), [adherents, fStatut, fConv, fEtab]);

  const actifs = useMemo(() => adherents.filter(a => a.statut === 'Actif'), [adherents]);

  /* ── KPI financiers ── */
  const finKpi = useMemo(() => {
    let collected = 0, expected = 0;
    const now = new Date(); const curM = now.getMonth() + 1;
    let thisMonth = 0;
    actifs.forEach(adh => {
      const months = moisActifs(adh, yearCots);
      expected += (adh.montant || 0) * months.length;
      months.forEach(m => {
        const ct = (yearCots[adh.id] || {})[m];
        if (ct?.status === 'paye') {
          collected += (adh.montant || 0);
          if (m === curM) thisMonth += (adh.montant || 0);
        }
      });
    });
    return {
      collected: Math.round(collected),
      expected:  Math.round(expected),
      thisMonth: Math.round(thisMonth),
      taux: expected > 0 ? Math.round(collected / expected * 100) : 0,
    };
  }, [actifs, yearCots]);

  const newThisMonth = useMemo(() => {
    const now = new Date();
    return adherents.filter(a => {
      if (!a.dateAdhesion) return false;
      const d = new Date(a.dateAdhesion);
      return d.getFullYear() === now.getFullYear() && d.getMonth() + 1 === now.getMonth() + 1;
    }).length;
  }, [adherents]);

  const etabsCovered = useMemo(() => new Set(actifs.map(a => a.etablissementId).filter(Boolean)).size, [actifs]);

  /* ── Données adhérents ── */
  const byConvention = useMemo(() => {
    const g = {};
    filtered.forEach(a => { const k = a.convention || 'Non renseigné'; g[k] = (g[k] || 0) + 1; });
    return Object.entries(g).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  const byEtab = useMemo(() => etablissements.map(e => {
    const count = filtered.filter(a => a.etablissementId === e.id).length;
    return { name: e.nom.length > 26 ? e.nom.slice(0, 26) + '…' : e.nom, fullName: e.nom, count, pct: filtered.length > 0 ? Math.round(count / filtered.length * 100) : 0 };
  }).filter(e => e.count > 0).sort((a, b) => b.count - a.count), [filtered, etablissements]);

  const byMetier = useMemo(() => {
    const g = {};
    filtered.forEach(a => { const k = a.metier || 'Non renseigné'; g[k] = (g[k] || 0) + 1; });
    return Object.entries(g).map(([name, value]) => ({ name: name.length > 22 ? name.slice(0, 22) + '…' : name, fullName: name, value }))
      .sort((a, b) => b.value - a.value).slice(0, 12);
  }, [filtered]);

  const byGenre = useMemo(() => {
    const g = { Femme: 0, Homme: 0, 'Non renseigné': 0 };
    filtered.forEach(a => {
      if (['Madame', 'F', 'Femme'].includes(a.genre))        g['Femme']++;
      else if (['Monsieur', 'M', 'Homme'].includes(a.genre)) g['Homme']++;
      else                                                    g['Non renseigné']++;
    });
    return Object.entries(g).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const evolutionAdhesions = useMemo(() => MOIS_COURT.map((label, idx) => {
    const m = idx + 1;
    return {
      label,
      count: filtered.filter(a => {
        if (!a.dateAdhesion) return false;
        const d = new Date(a.dateAdhesion);
        return d.getFullYear() === selectedYear && d.getMonth() + 1 === m;
      }).length,
    };
  }), [filtered, selectedYear]);

  /* ── Données finance ── */
  const monthlyFinance = useMemo(() => MOIS_COURT.map((label, idx) => {
    const m = idx + 1; let cur = 0, prev = 0;
    actifs.forEach(adh => {
      const cc = (yearCots[adh.id]     || {})[m];
      const pc = (prevYearCots[adh.id] || {})[m];
      if (cc?.status === 'paye') cur  += (adh.montant || 0);
      if (pc?.status === 'paye') prev += (adh.montant || 0);
    });
    return { label, [String(selectedYear)]: Math.round(cur), [String(selectedYear - 1)]: Math.round(prev) };
  }), [actifs, yearCots, prevYearCots, selectedYear]);

  const cotsByConvention = useMemo(() => {
    const g = {};
    actifs.forEach(adh => {
      const k = adh.convention || 'Non renseigné';
      const c = Object.values(yearCots[adh.id] || {}).filter(x => x.status === 'paye').length * (adh.montant || 0);
      g[k] = (g[k] || 0) + c;
    });
    return Object.entries(g).map(([name, value]) => ({ name, value: Math.round(value) })).sort((a, b) => b.value - a.value);
  }, [actifs, yearCots]);

  const paymentStatus = useMemo(() => {
    const curM = new Date().getMonth() + 1;
    let aJour = 0, retard = 0, defaut = 0;
    actifs.forEach(adh => {
      const paid = Object.values(yearCots[adh.id] || {}).filter(x => x.status === 'paye').length;
      const pct  = curM > 0 ? paid / curM : 0;
      if (pct >= 0.9) aJour++; else if (pct >= 0.5) retard++; else defaut++;
    });
    return [
      { name: 'À jour (≥90%)',       value: aJour,  color: '#2e7d32' },
      { name: 'En retard (50–89%)',   value: retard, color: '#ff9800' },
      { name: 'En défaut (<50%)',     value: defaut, color: FO_RED },
    ].filter(x => x.value > 0);
  }, [actifs, yearCots]);

  const tauxByEtab = useMemo(() => etablissements.map(etab => {
    const members = actifs.filter(a => a.etablissementId === etab.id);
    if (!members.length) return null;
    let c = 0, e = 0;
    members.forEach(adh => {
      const months = moisActifs(adh, yearCots);
      e += (adh.montant || 0) * months.length;
      months.forEach(m => { const ct = (yearCots[adh.id] || {})[m]; if (ct?.status === 'paye') c += (adh.montant || 0); });
    });
    const taux = e > 0 ? Math.round(c / e * 100) : 0;
    return { name: etab.nom.length > 26 ? etab.nom.slice(0, 26) + '…' : etab.nom, taux, color: taux >= 70 ? '#2e7d32' : taux >= 40 ? '#ff9800' : FO_RED };
  }).filter(Boolean).sort((a, b) => b.taux - a.taux), [actifs, etablissements, yearCots]);

  /* ── Ancienneté syndicale ── */
  const byAnciennete = useMemo(() => {
    const now = new Date();
    const trs = [
      { label: '< 1 an',   min: 0,  max: 1 },
      { label: '1–3 ans',  min: 1,  max: 3 },
      { label: '3–5 ans',  min: 3,  max: 5 },
      { label: '5–10 ans', min: 5,  max: 10 },
      { label: '> 10 ans', min: 10, max: 9999 },
    ];
    const counts = trs.map(t => ({ label: t.label, value: 0 }));
    filtered.forEach(a => {
      if (!a.dateAdhesion) return;
      const yrs = (now - new Date(a.dateAdhesion)) / (365.25 * 86400000);
      if (isNaN(yrs) || yrs < 0) return;
      const i = trs.findIndex(t => yrs >= t.min && yrs < t.max);
      if (i >= 0) counts[i].value++;
    });
    return counts;
  }, [filtered]);

  /* ── Mode paiement ── */
  const byModePaiement = useMemo(() => {
    const g = {};
    filtered.forEach(a => { const k = a.modePaiement || 'Non renseigné'; g[k] = (g[k] || 0) + 1; });
    return Object.entries(g).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  /* ── KPI depuis relevés BFC ── */
  const comptaKPI = useMemo(() => {
    let collected = 0, impayes = 0;
    for (let m = 1; m <= 12; m++) {
      const ops = comptabilite[selectedYear]?.[m]?.operations || [];
      collected += ops.filter(o => o.type === 'A').reduce((s, o) => s + (o.credit || 0), 0);
      impayes   += ops.filter(o => o.type === 'B').reduce((s, o) => s + (o.debit  || 0), 0);
    }
    return { collected: Math.round(collected), impayes: Math.round(impayes) };
  }, [comptabilite, selectedYear]);

  /* ── Évolution multi-années BFC ── */
  const multiYearFinance = useMemo(() => MOIS_COURT.map((label, idx) => {
    const m = idx + 1; const entry = { label };
    for (let y = selectedYear - 2; y <= selectedYear; y++) {
      const ops = comptabilite[y]?.[m]?.operations || [];
      entry[String(y)] = Math.round(ops.filter(o => o.type === 'A').reduce((s, o) => s + (o.credit || 0), 0));
    }
    return entry;
  }), [comptabilite, selectedYear]);

  const handleExportPDF = useCallback(async () => {
    if (!dashRef.current || exportingPDF) return;
    setExportingPDF(true);
    try {
      const canvas = await html2canvas(dashRef.current, {
        scale: 1.5, backgroundColor: '#ffffff', useCORS: true,
        ignoreElements: el => el.classList.contains('no-print'),
      });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const PW = 210, MARGIN = 10, HDR = 22, CONTENT_H = 297 - HDR - MARGIN;
      const CW = PW - 2 * MARGIN; const scale = canvas.width / CW;
      const pageHpx = CONTENT_H * scale; const numPg = Math.ceil(canvas.height / pageHpx);
      const today = new Date().toLocaleDateString('fr-FR');
      for (let p = 0; p < numPg; p++) {
        if (p > 0) pdf.addPage();
        const sy = p * pageHpx; const slH = Math.min(pageHpx, canvas.height - sy);
        const pc = document.createElement('canvas'); pc.width = canvas.width; pc.height = slH;
        const ctx = pc.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, pc.width, pc.height);
        ctx.drawImage(canvas, 0, sy, canvas.width, slH, 0, 0, canvas.width, slH);
        const slHmm = slH / scale;
        pdf.setFillColor(204, 0, 0); pdf.rect(0, 0, PW, HDR, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12); pdf.setFont('helvetica', 'bold');
        pdf.text(`SDASSP FO Réunion — Statistiques ${selectedYear}`, MARGIN, 15);
        pdf.setFontSize(8); pdf.setFont('helvetica', 'normal');
        pdf.text(`${today} — Page ${p + 1}/${numPg}`, PW - MARGIN, 15, { align: 'right' });
        pdf.setTextColor(0, 0, 0);
        pdf.addImage(pc.toDataURL('image/jpeg', 0.82), 'JPEG', MARGIN, HDR + 2, CW, slHmm);
      }
      pdf.save(`statistiques-sdassp-fo-${selectedYear}.pdf`);
    } catch (err) { alert('Erreur export PDF : ' + err.message); }
    finally { setExportingPDF(false); }
  }, [dashRef, selectedYear, exportingPDF]);

  const suspendus = adherents.filter(a => a.statut === 'Suspendu');
  const radies    = adherents.filter(a => a.statut === 'Radié');
  const tauxColor = finKpi.taux >= 70 ? '#2e7d32' : finKpi.taux >= 40 ? '#ff9800' : FO_RED;

  return (
    <div ref={dashRef}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .card { page-break-inside: avoid; box-shadow: none !important; border: 1px solid #ddd !important; }
          body { font-size: 12px; }
        }
      `}</style>

      {/* ── EN-TÊTE ── */}
      <div className="page-header">
        <div>
          <div className="page-title">📈 Statistiques — {selectedYear}</div>
          <div className="page-subtitle">Tableau de bord SDASSP FO Réunion</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} className="no-print">
          <select className="form-control" style={{ width: 'auto' }} value={selectedYear}
            onChange={e => setYear(parseInt(e.target.value))}>
            {[selectedYear - 2, selectedYear - 1, selectedYear, selectedYear + 1].map(y =>
              <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-primary" onClick={handleExportPDF} disabled={exportingPDF}>
            {exportingPDF ? '⏳ Génération…' : '🖨️ Exporter rapport PDF'}
          </button>
        </div>
      </div>

      {/* ── FILTRES ── */}
      <div className="card mb-4 no-print" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-control" style={{ width: 'auto', fontSize: 13 }}
            value={fStatut} onChange={e => setFStatut(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="Actif">Actifs</option>
            <option value="Suspendu">Suspendus</option>
            <option value="Inactif">Inactifs</option>
            <option value="Radié">Radiés</option>
          </select>
          <select className="form-control" style={{ width: 'auto', fontSize: 13 }}
            value={fConv} onChange={e => setFConv(e.target.value)}>
            <option value="all">Toutes conventions</option>
            {conventions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="form-control" style={{ width: 'auto', fontSize: 13 }}
            value={fEtab} onChange={e => setFEtab(e.target.value)}>
            <option value="all">Tous les établissements</option>
            {etablissements.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
          </select>
          {(fStatut !== 'Actif' || fConv !== 'all' || fEtab !== 'all') && (
            <button className="btn btn-outline btn-sm"
              onClick={() => { setFStatut('Actif'); setFConv('all'); setFEtab('all'); }}>
              ✕ Réinitialiser
            </button>
          )}
          <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
            {filtered.length} adhérent{filtered.length > 1 ? 's' : ''} affiché{filtered.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* ── KPI CARDS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <KpiCard label="Adhérents actifs"   value={actifs.length}           sub={`${suspendus.length} susp. · ${radies.length} radiés`} color="#2e7d32" />
        <KpiCard label="Nouveaux ce mois"   value={newThisMonth}            sub="Adhésions récentes"                                   color="#1565c0" />
        <KpiCard label={`Collecté ${selectedYear}`}
          value={`${finKpi.collected} €`}
          sub={`${finKpi.taux}% de l'objectif`}
          color={FO_RED} />
        <KpiCard label="Taux recouvrement"  value={`${finKpi.taux}%`}      sub={`Objectif : ${finKpi.expected} €`}                   color={tauxColor} />
        <KpiCard label="Structures couvertes" value={etabsCovered}          sub={`/ ${etablissements.length} établissements`}          color="#6a1b9a" />
        {comptaKPI.impayes > 0
          ? <KpiCard label="Impayés BFC" value={`${comptaKPI.impayes} €`} sub="Prélèvements rejetés" color="#991b1b" />
          : <KpiCard label="Encaissé ce mois" value={`${finKpi.thisMonth} €`} sub="Mois en cours" color="#e65100" />
        }
      </div>

      {/* ── SOUS-ONGLETS ── */}
      <div className="tabs mb-4 no-print">
        {[['adherents', '👥 Adhérents'], ['finance', '💰 Finance']].map(([v, l]) => (
          <button key={v} className={`tab-btn${mainTab === v ? ' active' : ''}`}
            onClick={() => setMainTab(v)}>{l}</button>
        ))}
      </div>

      {/* ══════════════ ONGLET ADHÉRENTS ══════════════ */}
      {mainTab === 'adherents' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>

            {/* Convention Pie */}
            <Block title="Répartition par convention collective"
              blockRef={refConv}
              onPNG={() => exportPNG(refConv, 'conventions.png')}
              onCSV={() => exportCSV(byConvention, 'conventions.csv')}>
              {byConvention.length === 0
                ? <div className="text-sm text-gray">Aucune donnée.</div>
                : <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={byConvention} cx="50%" cy="50%" outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={true}>
                        {byConvention.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<Tip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </Block>

            {/* Genre Donut */}
            <Block title="Répartition par genre"
              blockRef={refGenre}
              onPNG={() => exportPNG(refGenre, 'genre.png')}
              onCSV={() => exportCSV(byGenre, 'genre.csv')}>
              {byGenre.length === 0
                ? <div className="text-sm text-gray">Aucune donnée.</div>
                : <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={byGenre} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {byGenre.map((_, i) => <Cell key={i} fill={GENRE_COLORS[i % GENRE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<Tip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </Block>
          </div>

          {/* Etablissements Bar horizontal */}
          <Block title="Adhérents par établissement (actifs)"
            blockRef={refEtab}
            onPNG={() => exportPNG(refEtab, 'etablissements.png')}
            onCSV={() => exportCSV(byEtab.map(e => ({ établissement: e.fullName, adhérents: e.count, pourcentage: `${e.pct}%` })), 'établissements.csv')}>
            {byEtab.length === 0
              ? <div className="text-sm text-gray">Aucun établissement avec adhérents.</div>
              : <ResponsiveContainer width="100%" height={Math.max(220, byEtab.length * 34)}>
                  <BarChart data={byEtab} layout="vertical" margin={{ left: 8, right: 36, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                    <Tooltip content={<Tip />} />
                    <Bar dataKey="count" name="Adhérents" radius={[0, 5, 5, 0]}>
                      {byEtab.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
            }
          </Block>

          {/* Métiers */}
          <Block title="Top 12 — Métiers / Corps de métier"
            blockRef={refMetier}
            onPNG={() => exportPNG(refMetier, 'metiers.png')}
            onCSV={() => exportCSV(byMetier.map(m => ({ metier: m.fullName, adhérents: m.value })), 'metiers.csv')}>
            {byMetier.length === 0
              ? <div className="text-sm text-gray">Aucune donnée.</div>
              : <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={byMetier} margin={{ left: 8, right: 8, bottom: 50, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, angle: -35, textAnchor: 'end' }} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip content={<Tip />} />
                    <Bar dataKey="value" name="Adhérents" radius={[4, 4, 0, 0]}>
                      {byMetier.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
            }
          </Block>

          {/* Évolution adhésions */}
          <Block title={`Nouvelles adhésions ${selectedYear} par mois`}
            blockRef={refEvol}
            onPNG={() => exportPNG(refEvol, 'evolution_adhesions.png')}
            onCSV={() => exportCSV(evolutionAdhesions, 'adhesions_mensuelles.csv')}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={evolutionAdhesions} margin={{ left: 8, right: 8, top: 4, bottom: 4 }}>
                <defs>
                  <linearGradient id="gradAdh" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={FO_RED} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={FO_RED} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="count" name="Nouvelles adhésions"
                  stroke={FO_RED} fill="url(#gradAdh)" strokeWidth={2} dot={{ r: 3, fill: FO_RED }} />
              </AreaChart>
            </ResponsiveContainer>
          </Block>

          {/* Ancienneté syndicale */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px,1fr))', gap:16 }}>
            <Block title="Ancienneté syndicale (années d'adhésion)"
              blockRef={refAnc}
              onPNG={() => exportPNG(refAnc, 'anciennete.png')}
              onCSV={() => exportCSV(byAnciennete, 'anciennete.csv')}>
              {byAnciennete.every(b => b.value === 0)
                ? <div style={{ padding:'20px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>
                    Renseigner les dates d'adhésion pour afficher ce graphique.
                  </div>
                : <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={byAnciennete} margin={{ left:8, right:8, top:4, bottom:4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize:11 }} />
                      <YAxis tick={{ fontSize:11 }} allowDecimals={false} />
                      <Tooltip content={<Tip />} />
                      <Bar dataKey="value" name="Adhérents" radius={[4,4,0,0]}>
                        {byAnciennete.map((_,i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
              }
            </Block>

            {/* Mode paiement */}
            <Block title="Mode de paiement des cotisations"
              blockRef={refMode}
              onPNG={() => exportPNG(refMode, 'mode_paiement.png')}
              onCSV={() => exportCSV(byModePaiement, 'mode_paiement.csv')}>
              {byModePaiement.length === 0
                ? <div className="text-sm text-gray">Aucune donnée.</div>
                : <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={byModePaiement} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                        {byModePaiement.map((_,i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<Tip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </Block>
          </div>
        </div>
      )}

      {/* ══════════════ ONGLET FINANCE ══════════════ */}
      {mainTab === 'finance' && (
        <div>
          {/* Évolution multi-années BFC réelle */}
          <Block title={`Cotisations encaissées ${selectedYear - 2}–${selectedYear} (relevés BFC)`}
            blockRef={refMulti}
            onPNG={() => exportPNG(refMulti, 'finance_multiannee.png')}
            onCSV={() => exportCSV(multiYearFinance, 'finance_multiannee.csv')}>
            {multiYearFinance.every(m => !m[String(selectedYear)] && !m[String(selectedYear-1)] && !m[String(selectedYear-2)])
              ? <div style={{ padding:'20px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>
                  Importez les relevés BFC pour afficher la comparaison multi-années.
                </div>
              : <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={multiYearFinance} margin={{ left:8, right:8, top:4, bottom:4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize:11 }} />
                    <YAxis tick={{ fontSize:11 }} tickFormatter={v => `${v}€`} />
                    <Tooltip content={<Tip money />} />
                    <Legend />
                    <Line type="monotone" dataKey={String(selectedYear)}     name={String(selectedYear)}     stroke={FO_RED}   strokeWidth={2} dot={{ r:3 }} />
                    <Line type="monotone" dataKey={String(selectedYear - 1)} name={String(selectedYear-1)} stroke="#1565c0" strokeWidth={2} dot={{ r:3 }} />
                    <Line type="monotone" dataKey={String(selectedYear - 2)} name={String(selectedYear-2)} stroke="#9e9e9e" strokeWidth={1} strokeDasharray="4 2" dot={{ r:2 }} />
                  </LineChart>
                </ResponsiveContainer>
            }
          </Block>

          {/* Encaissements mensuels N vs N-1 (pointage) */}
          <Block title={`Encaissements mensuels — ${selectedYear} vs ${selectedYear - 1}`}
            blockRef={refFin}
            onPNG={() => exportPNG(refFin, 'encaissements.png')}
            onCSV={() => exportCSV(monthlyFinance, 'encaissements_mensuels.csv')}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyFinance} margin={{ left: 8, right: 8, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}€`} />
                <Tooltip content={<Tip money />} />
                <Legend />
                <Bar dataKey={String(selectedYear)}     name={String(selectedYear)}     fill={FO_RED} radius={[3, 3, 0, 0]} />
                <Bar dataKey={String(selectedYear - 1)} name={String(selectedYear - 1)} fill="#ccc"   radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Block>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            {/* Statut paiement */}
            <Block title="Statut de paiement des adhérents actifs"
              blockRef={refPay}
              onPNG={() => exportPNG(refPay, 'statut_paiement.png')}
              onCSV={() => exportCSV(paymentStatus.map(({ name, value }) => ({ statut: name, adherents: value })), 'statut_paiement.csv')}>
              {paymentStatus.length === 0
                ? <div className="text-sm text-gray">Aucune donnée.</div>
                : <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={paymentStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}>
                        {paymentStatus.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip content={<Tip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </Block>

            {/* Cotisations par convention */}
            <Block title="Cotisations collectées par convention"
              blockRef={refCotCcn}
              onPNG={() => exportPNG(refCotCcn, 'cots_convention.png')}
              onCSV={() => exportCSV(cotsByConvention, 'cots_convention.csv')}>
              {cotsByConvention.length === 0
                ? <div className="text-sm text-gray">Aucune donnée.</div>
                : <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={cotsByConvention} cx="50%" cy="50%" outerRadius={85}
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}>
                        {cotsByConvention.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<Tip money />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </Block>
          </div>

          {/* Taux de recouvrement par établissement */}
          <Block title="Taux de recouvrement par établissement"
            blockRef={refTaux}
            onPNG={() => exportPNG(refTaux, 'taux_recouvrement.png')}
            onCSV={() => exportCSV(tauxByEtab.map(e => ({ etablissement: e.name, taux: `${e.taux}%` })), 'recouvrement.csv')}>
            {tauxByEtab.length === 0
              ? <div className="text-sm text-gray">Aucune donnée.</div>
              : <ResponsiveContainer width="100%" height={Math.max(220, tauxByEtab.length * 34)}>
                  <BarChart data={tauxByEtab} layout="vertical" margin={{ left: 8, right: 50, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                    <Tooltip content={<Tip />} formatter={v => [`${v}%`, 'Taux']} />
                    <Bar dataKey="taux" name="Taux (%)" radius={[0, 5, 5, 0]}>
                      {tauxByEtab.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
            }
          </Block>
        </div>
      )}

      {/* ── SOLDE BANCAIRE ── */}
      <div className="card mt-4">
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>💰 Solde bancaire</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input className="form-control" style={{ width: 180, fontWeight: 700, fontSize: 16 }}
            type="number" step="0.01" value={soldeEdit}
            onChange={e => setSoldeEdit(e.target.value)}
            onBlur={() => { const v = parseFloat(soldeEdit); if (!isNaN(v)) setSolde(v); }} />
          <span style={{ fontSize: 13, color: '#888' }}>€ — Compte SDASSP 00801428500</span>
        </div>
      </div>
    </div>
  );
}
